import { getUser } from "../utils/authService";
import { useState, useEffect } from "react";
import { 
  Truck, Plus, Search, Filter, Download, Upload, ArrowRight, ArrowLeft, 
  Edit, Trash2, History, MapPin, Calendar, Fuel, Settings, CheckCircle,
  AlertTriangle, Clock, Building2
} from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const MasterEquipment = () => {
  const [equipment, setEquipment] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  
  // Dialogs
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [deploymentHistory, setDeploymentHistory] = useState([]);
  const [selectedProjectForDeploy, setSelectedProjectForDeploy] = useState("");
  const [selectedProjectForImport, setSelectedProjectForImport] = useState("");
  const [deployRemarks, setDeployRemarks] = useState("");

  const currentUser = getUser() || {};
  const isMasterAdmin = currentUser.email === "shiva.mittal@kpminfra.com";

  const [formData, setFormData] = useState({
    name: "",
    equipment_id: "",
    equipment_type: "Heavy",
    category: "",
    make: "",
    model: "",
    year_of_manufacture: "",
    capacity: "",
    fuel_type: "Diesel",
    tank_capacity: "",
    avg_consumption: "",
    owner_type: "Own",
    hire_rate: "",
    registration_number: "",
    insurance_expiry: "",
    fitness_expiry: ""
  });

  useEffect(() => {
    fetchEquipment();
    fetchProjects();
  }, []);

  const fetchEquipment = async () => {
    try {
      const response = await apiClient.get("/master-equipment");
      setEquipment(response.data);
    } catch (error) {
      toast.error("Failed to fetch equipment");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get("/projects");
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects");
    }
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        year_of_manufacture: formData.year_of_manufacture ? parseInt(formData.year_of_manufacture) : null,
        tank_capacity: parseFloat(formData.tank_capacity) || 0,
        avg_consumption: parseFloat(formData.avg_consumption) || 0,
        hire_rate: parseFloat(formData.hire_rate) || 0
      };
      await apiClient.post("/master-equipment", data);
      toast.success("Equipment added to master registry");
      setAddDialogOpen(false);
      resetForm();
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add equipment");
    }
  };

  const handleUpdateEquipment = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        year_of_manufacture: formData.year_of_manufacture ? parseInt(formData.year_of_manufacture) : null,
        tank_capacity: parseFloat(formData.tank_capacity) || 0,
        avg_consumption: parseFloat(formData.avg_consumption) || 0,
        hire_rate: parseFloat(formData.hire_rate) || 0
      };
      await apiClient.put(`/master-equipment/${selectedEquipment.id}`, data);
      toast.success("Equipment updated");
      setEditDialogOpen(false);
      resetForm();
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update equipment");
    }
  };

  const handleDeleteEquipment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this equipment?")) return;
    try {
      await apiClient.delete(`/master-equipment/${id}`);
      toast.success("Equipment deleted");
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete equipment");
    }
  };

  const handleDeployEquipment = async () => {
    if (!selectedProjectForDeploy) {
      toast.error("Please select a project");
      return;
    }
    try {
      await apiClient.post(
        `/master-equipment/${selectedEquipment.id}/deploy?project_id=${selectedProjectForDeploy}&remarks=${encodeURIComponent(deployRemarks)}`
      );
      toast.success("Equipment deployed successfully");
      setDeployDialogOpen(false);
      setSelectedProjectForDeploy("");
      setDeployRemarks("");
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deploy equipment");
    }
  };

  const handleReturnEquipment = async (equip) => {
    if (!window.confirm(`Return ${equip.name} from ${equip.current_project_name}?`)) return;
    try {
      await apiClient.post(`/master-equipment/${equip.id}/return`);
      toast.success("Equipment returned to master registry");
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to return equipment");
    }
  };

  const handleViewHistory = async (equip) => {
    setSelectedEquipment(equip);
    try {
      const response = await apiClient.get(`/master-equipment/${equip.id}/history`);
      setDeploymentHistory(response.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error("Failed to fetch history");
    }
  };

  const handleImportFromProject = async () => {
    if (!selectedProjectForImport) {
      toast.error("Please select a project");
      return;
    }
    try {
      const response = await apiClient.post(`/master-equipment/import-from-project/${selectedProjectForImport}`);
      toast.success(response.data.message);
      setImportDialogOpen(false);
      setSelectedProjectForImport("");
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import equipment");
    }
  };

  const openEditDialog = (equip) => {
    setSelectedEquipment(equip);
    setFormData({
      name: equip.name,
      equipment_id: equip.equipment_id,
      equipment_type: equip.equipment_type || "Heavy",
      category: equip.category || "",
      make: equip.make || "",
      model: equip.model || "",
      year_of_manufacture: equip.year_of_manufacture || "",
      capacity: equip.capacity || "",
      fuel_type: equip.fuel_type || "Diesel",
      tank_capacity: equip.tank_capacity || "",
      avg_consumption: equip.avg_consumption || "",
      owner_type: equip.owner_type || "Own",
      hire_rate: equip.hire_rate || "",
      registration_number: equip.registration_number || "",
      insurance_expiry: equip.insurance_expiry || "",
      fitness_expiry: equip.fitness_expiry || "",
      status: equip.status || "Available"
    });
    setEditDialogOpen(true);
  };

  const openDeployDialog = (equip) => {
    setSelectedEquipment(equip);
    setDeployDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      equipment_id: "",
      equipment_type: "Heavy",
      category: "",
      make: "",
      model: "",
      year_of_manufacture: "",
      capacity: "",
      fuel_type: "Diesel",
      tank_capacity: "",
      avg_consumption: "",
      owner_type: "Own",
      hire_rate: "",
      registration_number: "",
      insurance_expiry: "",
      fitness_expiry: ""
    });
    setSelectedEquipment(null);
  };

  const filteredEquipment = equipment.filter(e => {
    const matchesSearch = e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         e.equipment_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || e.status === filterStatus;
    const matchesType = filterType === "all" || e.equipment_type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (status) => {
    const styles = {
      "Available": "bg-success/10 text-success",
      "Deployed": "bg-blue-100 text-blue-700",
      "Under Maintenance": "bg-warning/10 text-warning",
      "Breakdown": "bg-error/10 text-error"
    };
    return styles[status] || "bg-slate-100 text-slate-600";
  };

  const stats = {
    total: equipment.length,
    available: equipment.filter(e => e.status === "Available").length,
    deployed: equipment.filter(e => e.status === "Deployed").length,
    maintenance: equipment.filter(e => e.status === "Under Maintenance" || e.status === "Breakdown").length
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-primary">Master Equipment Registry</h1>
          <p className="text-slate-600 mt-1">Central repository of all machinery and equipment</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setImportDialogOpen(true)}
            className="btn-outline flex items-center space-x-2"
          >
            <Upload className="w-4 h-4" />
            <span>Import from Project</span>
          </button>
          <button
            onClick={() => { resetForm(); setAddDialogOpen(true); }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Equipment</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">Total Equipment</p>
              <p className="text-2xl font-bold text-primary mt-1">{stats.total}</p>
            </div>
            <Truck className="w-8 h-8 text-primary/30" />
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">Available</p>
              <p className="text-2xl font-bold text-success mt-1">{stats.available}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-success/30" />
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">Deployed</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{stats.deployed}</p>
            </div>
            <Building2 className="w-8 h-8 text-blue-600/30" />
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">Maintenance</p>
              <p className="text-2xl font-bold text-warning mt-1">{stats.maintenance}</p>
            </div>
            <Settings className="w-8 h-8 text-warning/30" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Status</option>
          <option value="Available">Available</option>
          <option value="Deployed">Deployed</option>
          <option value="Under Maintenance">Under Maintenance</option>
          <option value="Breakdown">Breakdown</option>
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Types</option>
          <option value="Heavy">Heavy</option>
          <option value="Light">Light</option>
          <option value="Vehicle">Vehicle</option>
          <option value="Tool">Tool</option>
        </select>
      </div>

      {/* Equipment Grid */}
      {filteredEquipment.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-sm">
          <Truck className="w-16 h-16 mx-auto mb-4 text-slate-400" />
          <h3 className="font-heading font-bold text-xl mb-2">No Equipment Found</h3>
          <p className="text-slate-600 mb-6">Add equipment to your master registry or import from existing projects</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEquipment.map((equip) => (
            <div key={equip.id} className="stat-card hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-heading font-bold text-lg text-primary">{equip.name}</h3>
                  <p className="text-sm font-mono text-slate-500">{equip.equipment_id}</p>
                </div>
                <span className={`px-2 py-1 rounded-sm text-xs font-medium ${getStatusBadge(equip.status)}`}>
                  {equip.status}
                </span>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Type:</span>
                  <span className="font-medium">{equip.equipment_type}</span>
                </div>
                {equip.make && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Make/Model:</span>
                    <span className="font-medium">{equip.make} {equip.model}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Fuel:</span>
                  <span className="font-medium">{equip.fuel_type}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Owner:</span>
                  <span className="font-medium">{equip.owner_type}</span>
                </div>
                {equip.current_project_name && (
                  <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                    <span className="text-slate-500 flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      Deployed at:
                    </span>
                    <span className="font-medium text-blue-600">{equip.current_project_name}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200">
                {equip.status === "Available" ? (
                  <button
                    onClick={() => openDeployDialog(equip)}
                    className="flex-1 btn-primary text-xs py-2 flex items-center justify-center space-x-1"
                  >
                    <ArrowRight className="w-3 h-3" />
                    <span>Deploy</span>
                  </button>
                ) : equip.status === "Deployed" ? (
                  <button
                    onClick={() => handleReturnEquipment(equip)}
                    className="flex-1 btn-outline text-xs py-2 flex items-center justify-center space-x-1"
                  >
                    <ArrowLeft className="w-3 h-3" />
                    <span>Return</span>
                  </button>
                ) : null}
                <button
                  onClick={() => handleViewHistory(equip)}
                  className="btn-outline text-xs py-2 px-3"
                  title="View History"
                >
                  <History className="w-3 h-3" />
                </button>
                <button
                  onClick={() => openEditDialog(equip)}
                  className="btn-outline text-xs py-2 px-3"
                  title="Edit"
                >
                  <Edit className="w-3 h-3" />
                </button>
                {isMasterAdmin && !equip.current_project_id && (
                  <button
                    onClick={() => handleDeleteEquipment(equip.id)}
                    className="btn-outline text-xs py-2 px-3 text-error border-error hover:bg-error/10"
                    title="Delete"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Equipment Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl">Add Equipment to Master Registry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddEquipment} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Equipment Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., JCB 3DX Backhoe"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Equipment ID *</label>
                <input
                  type="text"
                  required
                  value={formData.equipment_id}
                  onChange={(e) => setFormData({ ...formData, equipment_id: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  placeholder="e.g., JCB-001"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Type</label>
                <select
                  value={formData.equipment_type}
                  onChange={(e) => setFormData({ ...formData, equipment_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Heavy">Heavy</option>
                  <option value="Light">Light</option>
                  <option value="Vehicle">Vehicle</option>
                  <option value="Tool">Tool</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Excavator, Crane, Truck"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Make</label>
                <input
                  type="text"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., JCB, TATA"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Model</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., 3DX"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Year</label>
                <input
                  type="number"
                  value={formData.year_of_manufacture}
                  onChange={(e) => setFormData({ ...formData, year_of_manufacture: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  placeholder="2020"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Fuel Type</label>
                <select
                  value={formData.fuel_type}
                  onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Diesel">Diesel</option>
                  <option value="Petrol">Petrol</option>
                  <option value="Electric">Electric</option>
                  <option value="CNG">CNG</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Tank Capacity (L)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.tank_capacity}
                  onChange={(e) => setFormData({ ...formData, tank_capacity: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Avg Consumption</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.avg_consumption}
                  onChange={(e) => setFormData({ ...formData, avg_consumption: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  placeholder="L/Hr"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Owner Type</label>
                <select
                  value={formData.owner_type}
                  onChange={(e) => setFormData({ ...formData, owner_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Own">Own</option>
                  <option value="Hired">Hired</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Hire Rate (₹/Hr)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.hire_rate}
                  onChange={(e) => setFormData({ ...formData, hire_rate: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Registration No.</label>
                <input
                  type="text"
                  value={formData.registration_number}
                  onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Insurance Expiry</label>
                <input
                  type="date"
                  value={formData.insurance_expiry}
                  onChange={(e) => setFormData({ ...formData, insurance_expiry: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Fitness Expiry</label>
                <input
                  type="date"
                  value={formData.fitness_expiry}
                  onChange={(e) => setFormData({ ...formData, fitness_expiry: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={() => setAddDialogOpen(false)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary">Add Equipment</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Equipment Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl">Edit Equipment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEquipment} className="space-y-4 mt-4">
            {/* Same form fields as add dialog */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Equipment Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Equipment ID *</label>
                <input
                  type="text"
                  required
                  value={formData.equipment_id}
                  onChange={(e) => setFormData({ ...formData, equipment_id: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Make</label>
                <input
                  type="text"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Model</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Available">Available</option>
                  <option value="Under Maintenance">Under Maintenance</option>
                  <option value="Breakdown">Breakdown</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={() => setEditDialogOpen(false)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary">Update Equipment</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deploy Dialog */}
      <Dialog open={deployDialogOpen} onOpenChange={setDeployDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl">Deploy Equipment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="text-sm text-slate-500">Equipment:</p>
              <p className="font-bold text-primary">{selectedEquipment?.name}</p>
              <p className="text-sm font-mono text-slate-600">{selectedEquipment?.equipment_id}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Select Project *</label>
              <select
                value={selectedProjectForDeploy}
                onChange={(e) => setSelectedProjectForDeploy(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">-- Select Project --</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Remarks</label>
              <textarea
                value={deployRemarks}
                onChange={(e) => setDeployRemarks(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                rows="2"
                placeholder="Optional deployment notes..."
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button onClick={() => setDeployDialogOpen(false)} className="btn-outline">Cancel</button>
              <button onClick={handleDeployEquipment} className="btn-primary">Deploy</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl">Deployment History</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="p-4 bg-slate-50 rounded-sm mb-4">
              <p className="font-bold text-primary">{selectedEquipment?.name}</p>
              <p className="text-sm font-mono text-slate-600">{selectedEquipment?.equipment_id}</p>
            </div>
            {deploymentHistory.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No deployment history</p>
            ) : (
              <div className="space-y-3">
                {deploymentHistory.map((h) => (
                  <div key={h.id} className="p-4 border border-slate-200 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-primary">{h.project_name}</span>
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                        h.status === "Active" ? "bg-success/10 text-success" : "bg-slate-100 text-slate-600"
                      }`}>
                        {h.status}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600 space-y-1">
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 mr-2" />
                        <span>Deployed: {h.deployed_date}</span>
                        {h.returned_date && <span className="ml-4">Returned: {h.returned_date}</span>}
                      </div>
                      <p>By: {h.deployed_by}</p>
                      {h.remarks && <p className="italic">{h.remarks}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import from Project Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl">Import Equipment from Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Import all equipment from an existing project to the master registry. 
              Equipment already in the registry will be skipped.
            </p>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Select Project</label>
              <select
                value={selectedProjectForImport}
                onChange={(e) => setSelectedProjectForImport(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">-- Select Project --</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button onClick={() => setImportDialogOpen(false)} className="btn-outline">Cancel</button>
              <button onClick={handleImportFromProject} className="btn-primary">Import Equipment</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterEquipment;
